import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useGame } from "@/context/GameContext";
import { isCharadesQuestion } from "@/lib/charades";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { X, ZoomIn, Pause, Play, RotateCcw, RefreshCw, Zap, Clock, Flag, Plus } from "lucide-react";
import { getDirectionalText } from "@/lib/textDirection";

const API = `${process.env.REACT_APP_BACKEND_URL || "https://backend-production-cfa1f.up.railway.app"}/api`;

/* ═══════════════════════════════════════════════════════════════════════════
   ALL LOGIC PRESERVED — UI redesigned: deep navy + glass morphism + Tajawal
   ═══════════════════════════════════════════════════════════════════════════ */

function AudioPlayer({ url }) {
  const audioRef = useRef(null);
  const [playing, setPlaying]   = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else         { a.play().catch(() => {}); setPlaying(true); }
  };

  const restart = () => {
    const a = audioRef.current;
    if (!a) return;
    a.currentTime = 0;
    a.play().catch(() => {});
    setPlaying(true);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    a.currentTime = ratio * duration;
    setProgress(ratio * 100);
  };

  const fmt = (s) => {
    if (!s || isNaN(s)) return "0:00";
    const m = Math.floor(s / 60), sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  return (
    <div style={{ flex:1, minHeight:0, width:"100%", display:"flex", justifyContent:"center", alignItems:"center" }}>
      <audio
        ref={audioRef}
        src={url}
        onTimeUpdate={e => {
          const a = e.target;
          if (a.duration) setProgress((a.currentTime / a.duration) * 100);
        }}
        onLoadedMetadata={e => setDuration(e.target.duration)}
        onEnded={() => setPlaying(false)}
      />
      <div style={{
        width: "100%", maxWidth: "480px",
        background: "rgba(10,4,6,0.80)", backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
        border: "1.5px solid rgba(245,200,66,0.28)",
        borderRadius: "20px", padding: "28px 28px 24px",
        boxShadow: "0 0 0 3px rgba(245,200,66,0.06), 0 16px 50px rgba(0,0,0,0.65)",
      }}>
        {/* Waveform icon */}
        <div style={{ textAlign:"center", marginBottom:"18px" }}>
          <span style={{ fontSize:"2.4rem", lineHeight:1 }}>🎵</span>
        </div>

        {/* Progress bar */}
        <div
          onClick={seek}
          style={{ cursor:"pointer", height:"6px", borderRadius:"99px", background:"rgba(255,255,255,0.10)", position:"relative", marginBottom:"10px" }}
        >
          <div style={{
            height:"100%", borderRadius:"99px",
            width: `${progress}%`,
            background: "linear-gradient(90deg, #D4BC30, #F5E99A)",
            transition: "width 0.25s linear",
          }} />
        </div>

        {/* Time */}
        <div style={{ display:"flex", justifyContent:"space-between", fontSize:"0.7rem", color:"rgba(245,200,66,0.45)", fontWeight:700, marginBottom:"20px", fontVariantNumeric:"tabular-nums" }}>
          <span>{fmt(audioRef.current?.currentTime)}</span>
          <span>{fmt(duration)}</span>
        </div>

        {/* Controls */}
        <div style={{ display:"flex", gap:"12px", justifyContent:"center", alignItems:"center" }}>
          {/* Restart */}
          <button
            onClick={restart}
            style={{ background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(245,200,66,0.18)", borderRadius:"50%", width:"42px", height:"42px", cursor:"pointer", color:"rgba(245,200,66,0.55)", display:"flex", alignItems:"center", justifyContent:"center" }}
          >
            <RotateCcw size={16} />
          </button>
          {/* Play / Pause */}
          <button
            onClick={toggle}
            style={{ background:"linear-gradient(135deg,#D4BC30,#F5E99A)", border:"none", borderRadius:"50%", width:"58px", height:"58px", cursor:"pointer", color:"#130104", display:"flex", alignItems:"center", justifyContent:"center", boxShadow:"0 0 24px rgba(245,200,66,0.35)" }}
          >
            {playing ? <Pause size={22} /> : <Play size={22} style={{ marginLeft:"2px" }} />}
          </button>
          {/* Spacer placeholder for symmetry */}
          <div style={{ width:"42px" }} />
        </div>
      </div>
    </div>
  );
}

const EXTRA_TIME_SECONDS = 25; // single source of truth for extra-time lifeline

export default function QuestionPage() {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { session, updateScore, gameSettings, switchTurn, teamScores, lifelinesState, consumeLifeline,
          userToken, selectedQuestions, saveGameSnapshot, gameMode, soloSetup } = useGame();
  const isSolo = gameMode === "solo";
  const { question, catName, slot, catIsSpecial, turnTeam } = state || {};
  // activeTeam is the team whose turn it was when the tile was clicked.
  // This is the ONLY source of truth for team identity inside this screen.
  // `slot` (button column 1 or 2) is intentionally NOT used for team decisions —
  // both columns carry the same difficulties and the active team is currentTurn, not the side.
  const activeTeam = turnTeam || slot; // fallback to slot for backward-compat only

  // Charades behavior belongs to the question type. Category metadata remains
  // useful for navigation, but must not decide whether a secret answer is shown.
  const isWordRound = isCharadesQuestion(question);

  const diff = question?.difficulty || 300;
  let TIMER_DURATION = gameSettings?.default_timer || 65;
  if (isWordRound && gameSettings?.word_timers) {
    TIMER_DURATION = gameSettings.word_timers[String(diff)] ?? TIMER_DURATION;
  }

  // Resume state injected by MyGamesPage when continuing a mid-question save
  const resumedTime     = state?._resumedRemainingTime ?? null;
  const resumedAnswer   = state?._resumedShowAnswer    ?? false;
  const isResuming      = resumedTime !== null;

  const [timeLeft, setTimeLeft]       = useState(resumedTime != null ? resumedTime : TIMER_DURATION);
  // Word rounds start paused; resumed games also start paused so host can press "متابعة المؤقت"
  const [timerOn, setTimerOn]         = useState(!isWordRound && !isResuming);
  const [roundReady, setRoundReady]   = useState(false);
  const [readyBadge, setReadyBadge]   = useState(false);   // transient "بدأت الجولة" toast
  const [showAnswer, setShowAnswer]   = useState(resumedAnswer);
  const [assigned, setAssigned]       = useState(false);
  const [scoredTeam, setScoredTeam]   = useState(null);
  const [tensionDone, setTensionDone] = useState(false);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [imgLoaded, setImgLoaded]     = useState(false);
  const [imgRetries, setImgRetries]   = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(question);
  const { text: questionText, dir: questionDir } = getDirectionalText(currentQuestion?.text);
  const { text: answerText, dir: answerDir } = getDirectionalText(currentQuestion?.answer);
  const [doubleActive, setDoubleActive]       = useState(state?.doubleActive || false);
  const [changingQ, setChangingQ]             = useState(false);
  const timerRef      = useRef(null);
  const pollRef       = useRef(null);
  const audioCtxRef   = useRef(null); // holds AudioContext for tension sound so we can stop it
  const oscListRef    = useRef([]);   // every scheduled oscillator — needed for instant stop
  const imgPreloadRef = useRef([]);   // Image objects kept alive so browser cache fills before render
  const imgElemRef    = useRef(null); // ref to the rendered <img> — used to detect already-cached images

  // ── Report modal state ──
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportReason, setReportReason]       = useState("");
  const [reportDetails, setReportDetails]     = useState("");
  const [reportSending, setReportSending]     = useState(false);
  const [reportSent, setReportSent]           = useState(false);

  const REPORT_REASONS = [
    { id: "wrong_answer",    label: "الإجابة الصحيحة غير صحيحة",        icon: "❌" },
    { id: "spelling_error",  label: "يوجد خطأ إملائي أو لغوي",          icon: "📝" },
    { id: "image_mismatch",  label: "الصورة لا تطابق السؤال أو الإجابة", icon: "🖼️" },
    { id: "unclear",         label: "السؤال غير واضح أو ملتبس",          icon: "🤔" },
    { id: "inaccurate_info", label: "المعلومة غير دقيقة أو قديمة",       icon: "📚" },
    { id: "duplicate",       label: "سؤال مكرر بشكل مزعج",              icon: "🚫" },
    { id: "inappropriate",   label: "محتوى غير مناسب",                   icon: "⚠️" },
    { id: "other",           label: "سبب آخر",                            icon: "📌" },
  ];

  const handleSendReport = async () => {
    if (!reportReason) return;
    const token = localStorage.getItem("hujjah_user_token") || userToken;
    if (!token) {
      toast.error("يجب تسجيل الدخول للإبلاغ عن السؤال");
      closeReportModal();
      return;
    }
    setReportSending(true);
    try {
      const res = await fetch(`${API}/questions/${currentQuestion.id}/report`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question_id: currentQuestion.id, reason: reportReason, details: reportDetails || null }),
      });
      if (res.status === 409) { setReportSent(true); return; }
      if (res.status === 401) { toast.error("انتهت جلسة دخولك — سجّل الدخول مجدداً"); closeReportModal(); return; }
      if (!res.ok) throw new Error(`${res.status}`);
      setReportSent(true);
    } catch { toast.error("تعذّر إرسال البلاغ، حاول لاحقاً"); }
    finally { setReportSending(false); }
  };

  const closeReportModal = () => {
    setShowReportModal(false);
    setTimeout(() => { setReportReason(""); setReportDetails(""); setReportSent(false); }, 300);
  };

  // Cleanup audio on unmount
  useEffect(() => {
    return () => { stopAudio(); };
  }, []); // eslint-disable-line

  // Preload BOTH question image and answer image the moment the question is known.
  // Refs keep the Image objects alive so GC cannot cancel the in-flight request.
  // Also reset imgLoaded so the skeleton shows until the new image is ready.
  useEffect(() => {
    if (currentQuestion?.image_url) { setImgLoaded(false); setImgRetries(0); }
    window.scrollTo({ top: 0, behavior: "instant" });
    // Only preload real network URLs — skip base64 data: URLs (they need no network)
    const urls = [currentQuestion?.image_url, currentQuestion?.answer_image_url]
      .filter(u => u && u.startsWith("http"));
    imgPreloadRef.current = urls.map(url => {
      const img = new Image();
      img.src = url;
      if (img.decode) img.decode().catch(() => {});
      return img;
    });

    // ── LAYER 1: check if question image is already cached RIGHT NOW ──
    // When the image is already in browser cache, img.complete is true immediately
    // after setting img.src — before any React handler can be attached.
    // Detect this synchronously and skip the skeleton entirely.
    if (currentQuestion?.image_url) {
      const probe = new Image();
      probe.src = currentQuestion.image_url;
      if (probe.complete && probe.naturalWidth > 0) {
        setImgLoaded(true);
      }
    }
  }, [currentQuestion?.id]); // eslint-disable-line

  // ── LAYER 2 (belt): after every render, check if the rendered <img> is already
  // complete but imgLoaded hasn't been set yet. Catches any timing edge-case that
  // Layer 1 misses (e.g. decode() delay, progressive JPEG, cross-origin timing).
  useEffect(() => {
    if (!currentQuestion?.image_url || imgLoaded) return;
    const el = imgElemRef.current;
    if (el && el.complete && el.naturalWidth > 0) {
      setImgLoaded(true);
    }
  }); // intentionally no dep array — stops running once imgLoaded = true

  // ── LAYER 3 (suspenders): poll every 120ms for up to 4 seconds as absolute last resort.
  // Handles browsers that delay img.complete (e.g. some WebKit quirks with SVGs).
  useEffect(() => {
    if (!currentQuestion?.image_url || imgLoaded) return;
    const interval = setInterval(() => {
      const el = imgElemRef.current;
      if (el && el.complete && el.naturalWidth > 0) {
        setImgLoaded(true);
        clearInterval(interval);
      }
    }, 120);
    const timeout = setTimeout(() => clearInterval(interval), 4000);
    return () => { clearInterval(interval); clearTimeout(timeout); };
  }, [currentQuestion?.id, imgLoaded]); // eslint-disable-line

  useEffect(() => {
    if (!timerOn || timeLeft <= 0) return;
    timerRef.current = setTimeout(() => setTimeLeft(t => t - 1), 1000);
    return () => clearTimeout(timerRef.current);
  }, [timeLeft, timerOn]);

  // Poll backend for player-ready signal (word/charades rounds only)
  useEffect(() => {
    if (!isWordRound || roundReady || !session?.id || !currentQuestion?.id) return;
    const qId = currentQuestion.id;
    const sId = session.id;
    const poll = async () => {
      try {
        const res = await fetch(`${API}/secret/${qId}/ready?session_id=${sId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.ready) {
          setRoundReady(true);
          setTimerOn(true);
          clearInterval(pollRef.current);
        }
      } catch {}
    };
    pollRef.current = setInterval(poll, 1500);
    return () => clearInterval(pollRef.current);
  }, [isWordRound, roundReady, currentQuestion?.id, session?.id]); // eslint-disable-line

  // Brief "بدأت الجولة" badge — auto-dismisses after 2 s
  useEffect(() => {
    if (!roundReady) return;
    setReadyBadge(true);
    const t = setTimeout(() => setReadyBadge(false), 2000);
    return () => clearTimeout(t);
  }, [roundReady]); // eslint-disable-line

  useEffect(() => {
    if (timeLeft === 10 && !tensionDone && timerOn) { setTensionDone(true); playTension(); }
    if (timeLeft === 0 && timerOn) {
      setTimerOn(false);
      playBuzz();
      toast.error("⏰ انتهى الوقت!", { duration: 3000 });
    }
  }, [timeLeft, timerOn, tensionDone]);

  // ── Auto-save: capture current question state so resume works after browser close ──
  useEffect(() => {
    if (!userToken || !session?.id || !currentQuestion) return;
    const cats = (() => { try { return JSON.parse(localStorage.getItem(`categories_${session.id}`) || "[]"); } catch { return []; } })();
    if (cats.length === 0) return; // categories not yet loaded, skip
    const snapshot = {
      session_id: session.id,
      status: "paused",
      team1_name: session.team1_name || "",
      team2_name: session.team2_name || "",
      team1_score: teamScores.team1,
      team2_score: teamScores.team2,
      current_turn: turnTeam || 1,
      categories: cats,
      used_tiles: [...selectedQuestions],
      lifelines: lifelinesState,
      used_question_ids: session.used_questions || [],
      double_team: 0,
      game_mode: gameMode || "standard",
      solo_player_name: isSolo ? (soloSetup?.playerName || session?.team1_name || "") : "",
      solo_difficulty: isSolo ? (soloSetup?.difficulty || null) : null,
      solo_target: isSolo ? (soloSetup?.target || null) : null,
      current_question_state: {
        question: currentQuestion,
        catId: state?.catId,
        catName: state?.catName,
        difficulty: currentQuestion?.difficulty || 300,
        slot: state?.slot,
        catIsSpecial: catIsSpecial,
        turnTeam: turnTeam,
        doubleActive: doubleActive,
        showAnswer: showAnswer,
        remainingTime: timerOn ? timeLeft : 0,
        tileKey: state?.catId ? `${state.catId}_${currentQuestion?.difficulty || 300}_${state?.slot}` : null,
      },
    };
    // Debounce 2s so rapid state changes don't spam the API
    const t = setTimeout(() => saveGameSnapshot(snapshot), 2000);
    return () => clearTimeout(t);
  }, [currentQuestion?.id, showAnswer]); // eslint-disable-line

  const stopAudio = () => {
    // Stop every scheduled oscillator immediately (osc.stop(0) = stop at time 0 = right now)
    oscListRef.current.forEach(osc => {
      try { osc.stop(0); } catch {}
      try { osc.disconnect(); } catch {}
    });
    oscListRef.current = [];
    // suspend() halts audio output synchronously; close() releases OS resources
    try {
      if (audioCtxRef.current && audioCtxRef.current.state !== "closed") {
        audioCtxRef.current.suspend().catch(() => {});
        audioCtxRef.current.close().catch(() => {});
      }
    } catch {}
    audioCtxRef.current = null;
  };

  const playTension = () => {
    stopAudio(); // stop any previous audio first
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtxRef.current = ctx;
      oscListRef.current = [];
      const schedule = (freq, t, dur) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "triangle"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.25, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + dur);
        osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + dur + 0.01);
        oscListRef.current.push(osc); // track for instant stop
      };
      for (let i = 0; i < 10; i++) schedule(i % 2 === 0 ? 830 : 600, i * 0.85, 0.35);
    } catch {}
  };

  const playBuzz = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const osc = ctx.createOscillator(); const gain = ctx.createGain();
      osc.connect(gain); gain.connect(ctx.destination);
      osc.type = "sawtooth"; osc.frequency.value = 150;
      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
      osc.start(ctx.currentTime); osc.stop(ctx.currentTime + 0.8);
    } catch {}
  };

  const playCorrect = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const schedule = (freq, t, dur) => {
        const osc = ctx.createOscillator(); const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.type = "sine"; osc.frequency.value = freq;
        gain.gain.setValueAtTime(0.3, ctx.currentTime + t);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + t + dur);
        osc.start(ctx.currentTime + t); osc.stop(ctx.currentTime + t + dur + 0.01);
      };
      [523, 659, 784, 1047].forEach((f, i) => schedule(f, i * 0.12, 0.25));
    } catch {}
  };

  const [revealed, setRevealed] = useState(false);
  const scoringRef = useRef(false); // ref guard — immune to React async state batching
  const handleReveal  = () => { setTimerOn(false); stopAudio(); setShowAnswer(true); setRevealed(true); };
  const handleAssign  = async (team) => {
    if (scoringRef.current) return;
    scoringRef.current = true;
    const pts = (currentQuestion?.difficulty || 300) * (doubleActive ? 2 : 1);
    try {
      await updateScore(team, pts, currentQuestion?.id);
      setScoredTeam(team);
      setAssigned(true);
      playCorrect();
      window.dispatchEvent(new Event("scoreUpdated"));
      toast.success(`+${pts} ✓${doubleActive ? " ×2 🔥" : ""}`, { duration: 2000 });
    } catch {
      scoringRef.current = false; // genuine failure before backend commit — allow retry
    }
  };
  const handleSkip    = () => setAssigned(true);
  const handleBack    = () => { stopAudio(); if (!isSolo) switchTurn(); navigate("/game"); };

  const handleChangeQuestion = async () => {
    if (!lifelinesState[activeTeam]?.changeQuestion || assigned || changingQ) return;
    setChangingQ(true);
    try {
      const token = localStorage.getItem("hujjah_user_token");
      const res = await fetch(
        `${API}/game/session/${session?.id}/question?category_id=${currentQuestion.category_id}&difficulty=${currentQuestion.difficulty}${session?.is_trial ? `&current_question_id=${currentQuestion.id}` : ""}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error();
      const newQ = await res.json();
      stopAudio();
      setCurrentQuestion(newQ);
      setImgLoaded(false);
      setImgRetries(0);
      setShowAnswer(false);
      setAssigned(false);
      setTensionDone(false);
      // Full timer reset — new question gets full duration
      const newDiff = newQ.difficulty || diff;
      let newDuration = gameSettings?.default_timer || 65;
      if (isWordRound && gameSettings?.word_timers) {
        newDuration = gameSettings.word_timers[String(newDiff)] ?? newDuration;
      }
      setTimeLeft(newDuration);
      if (isWordRound) {
        setRoundReady(false);
        setTimerOn(false);
      } else {
        setTimerOn(true);
      }
      consumeLifeline(activeTeam, "changeQuestion");
      toast.success("🔄 تم تغيير السؤال");
    } catch {
      toast.error("تعذّر تغيير السؤال");
    } finally {
      setChangingQ(false);
    }
  };

  const handleDoublePoints = () => {
    if (doubleActive) return;
    if (!lifelinesState[activeTeam]?.doublePoints) {
      toast("مضاعفة النقاط مستخدمة مسبقاً", { icon: "⚡" });
      return;
    }
    toast("مضاعفة النقاط تُفعَّل قبل فتح السؤال من البورد", { icon: "⚡" });
  };

  const handleExtraTime = () => {
    if (!lifelinesState[activeTeam]?.extraTime || assigned) return;
    setTimeLeft(prev => prev + EXTRA_TIME_SECONDS);
    consumeLifeline(activeTeam, "extraTime");
    toast.success(`⏱ تمت إضافة ${EXTRA_TIME_SECONDS} ثانية!`);
  };

  if (!question) {
    navigate("/game");
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
        background: "#0a0502",
      }}>
        <div style={{ width: 40, height: 40, border: "3px solid rgba(241,225,148,0.15)", borderTopColor: "#F1E194", borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
        <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  /* ── Derived ── */
  const pct      = (timeLeft / TIMER_DURATION) * 100;
  const R        = 30;
  const circ     = 2 * Math.PI * R;
  const dash     = circ * (1 - pct / 100);
  const timerCol = timeLeft > 20 ? "#43e97b" : timeLeft > 10 ? "#f5c842" : "#ff6b6b";
  const isSecret = isWordRound;
  const secretUrl = `${window.location.origin}/secret/${currentQuestion?.id}?session=${session?.id}`;

  // Host can also trigger "جاهز" directly from the main screen (same effect as phone)
  const handleHostReady = async () => {
    if (roundReady) return;
    setRoundReady(true);
    setTimerOn(true);
    // Best-effort: also mark in backend so any late phone tap is idempotent
    try {
      await fetch(`${API}/secret/${currentQuestion?.id}/ready?session_id=${session?.id}`, { method: "POST" });
    } catch {}
  };
  const isTeam1  = activeTeam === 1;
  const activeTeamName = isTeam1 ? session?.team1_name : session?.team2_name;

  const diffBadge = question.difficulty === 300
    ? { label:"سهل",   color:"#43e97b", bg:"rgba(67,233,123,0.12)", border:"rgba(67,233,123,0.30)" }
    : question.difficulty === 600
    ? { label:"متوسط", color:"#f5c842", bg:"rgba(245,200,66,0.12)",  border:"rgba(245,200,66,0.30)"  }
    : { label:"صعب",   color:"#ff6b6b", bg:"rgba(255,107,107,0.12)", border:"rgba(255,107,107,0.30)" };

  const hasAnswerImg = !!currentQuestion?.answer_image_url;
  const hasAnswerTxt = !!currentQuestion?.answer;

  /* ── Reusable glass style ── */
  const glass = {
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.12)",
    backdropFilter: "blur(16px)",
    WebkitBackdropFilter: "blur(16px)",
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
  };

  return (
    <div
      className="min-h-screen flex flex-col select-none question-page-root"
      style={{
        minHeight: "100svh",
        background: "linear-gradient(rgba(10,0,0,0.62), rgba(10,0,0,0.80)), url('https://images.pexels.com/photos/159862/art-school-of-athens-raphael-italian-painter-fresco-159862.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920') center/cover fixed no-repeat",
        fontFamily: "'Tajawal', 'Cairo', sans-serif",
        direction: "rtl",
        position: "relative",
      }}
    >
      {/* Tajawal font */}
      <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap" />

      <style>{`
        @keyframes fadeInScale {
          from { opacity:0; transform:scale(0.88); }
          to   { opacity:1; transform:scale(1); }
        }
        @keyframes fadeIn {
          from { opacity:0; }
          to   { opacity:1; }
        }
        @keyframes answerPop {
          0%  { opacity:0; transform:scale(0.80); }
          70% { transform:scale(1.04); opacity:1; }
          100%{ transform:scale(1);   opacity:1; }
        }
        @keyframes contentFadeIn {
          from { opacity:0; transform:translateY(8px); }
          to   { opacity:1; transform:translateY(0); }
        }
        @keyframes imgReveal {
          from { opacity:0; transform:scale(0.96); }
          to   { opacity:1; transform:scale(1); }
        }
        .answer-reveal { animation: contentFadeIn 0.38s ease forwards; }
        .answer-img-reveal { animation: imgReveal 0.42s cubic-bezier(0.34,1.2,0.64,1) forwards; }
        @keyframes glowPulse {
          0%,100%{ box-shadow:0 0 20px rgba(67,233,123,0.3); }
          50%    { box-shadow:0 0 40px rgba(67,233,123,0.6); }
        }
        @keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes turnPulse { 0%,100%{opacity:1;} 50%{opacity:0.25;} }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        .report-modal-wrap  { animation: fadeIn 0.20s ease forwards; }
        .report-modal-box   { animation: fadeInScale 0.28s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .report-reason-btn:hover { background: rgba(245,200,66,0.14) !important; border-color: rgba(245,200,66,0.45) !important; }
        .report-reason-btn.selected { background: rgba(245,200,66,0.18) !important; border-color: rgba(245,200,66,0.70) !important; }
        .answer-pop         { animation: answerPop 0.50s cubic-bezier(0.34,1.56,0.64,1) 0.12s both; }
        .reveal-btn:hover   { transform:translateY(-2px) !important; }
        .next-btn:hover     { background:rgba(255,255,255,0.15) !important; transform:translateY(-1px); }
        @keyframes readyBadgePop {
          0%  { opacity:0; transform:translateY(-10px) scale(0.88); }
          60% { transform:translateY(2px) scale(1.04); opacity:1; }
          100%{ transform:translateY(0) scale(1); opacity:1; }
        }
        .ready-badge { animation: readyBadgePop 0.32s cubic-bezier(0.34,1.56,0.64,1) forwards; }
        .lifeline-btn { transition: transform 0.20s cubic-bezier(0.34,1.2,0.64,1), box-shadow 0.20s ease !important; }
        .lifeline-btn:not(.ll-used):hover  { transform: translateY(-3px) scale(1.05) !important; }
        .lifeline-btn:not(.ll-used):active { transform: scale(0.96) !important; transition-duration: 0.08s !important; }
      `}</style>

      {/* ════════════ RESUME BANNER — shown when game was resumed mid-question ════════════ */}
      {isResuming && !timerOn && !assigned && (
        <div style={{
          position: "fixed", top: 0, left: 0, right: 0, zIndex: 80,
          background: "linear-gradient(135deg, rgba(91,14,20,0.97), rgba(30,4,8,0.97))",
          borderBottom: "1.5px solid rgba(241,225,148,0.28)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          padding: "14px 20px",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "14px",
          fontFamily: "'Tajawal','Cairo',sans-serif",
          boxShadow: "0 4px 32px rgba(0,0,0,0.6)",
        }}>
          <span style={{ color: "rgba(241,225,148,0.85)", fontSize: "0.90rem", fontWeight: 700 }}>
            ⏸ المباراة استُكملت من حيث توقفت — المؤقت متوقف
          </span>
          <button
            onClick={() => setTimerOn(true)}
            style={{
              background: "linear-gradient(135deg, #F5E99A, #D4BC30)",
              color: "#130104", border: "none", borderRadius: "50px",
              padding: "8px 22px", fontSize: "0.88rem", fontWeight: 900,
              cursor: "pointer", fontFamily: "'Tajawal','Cairo',sans-serif",
              boxShadow: "0 4px 18px rgba(241,225,148,0.35)",
            }}
          >
            ▶ متابعة المؤقت
          </button>
        </div>
      )}

      {/* ════════════ ZOOM MODAL ════════════ */}
      {zoomedImage && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center"
          style={{ background:"rgba(0,0,0,0.96)", backdropFilter:"blur(10px)", WebkitBackdropFilter:"blur(10px)" }}
          onClick={() => setZoomedImage(null)}
        >
          <div className="relative max-w-5xl w-full px-4" onClick={e => e.stopPropagation()}>
            <button
              style={{ position:"absolute", top:"-40px", left:"16px", color:"rgba(255,255,255,0.6)", background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:"6px", fontSize:"0.9rem" }}
              onClick={() => setZoomedImage(null)}
            >
              <X size={18}/> إغلاق
            </button>
            <img src={zoomedImage} alt="zoomed" style={{ maxWidth:"100%", maxHeight:"88vh", objectFit:"contain", borderRadius:"18px", margin:"0 auto", display:"block", boxShadow:"0 0 100px rgba(0,0,0,0.95)" }} />
          </div>
        </div>
      )}

      {/* ════════════ REPORT MODAL ════════════ */}
      {showReportModal && (
        <div
          className="report-modal-wrap"
          style={{ position:"fixed", inset:0, zIndex:70, background:"rgba(0,0,0,0.82)", backdropFilter:"blur(8px)", WebkitBackdropFilter:"blur(8px)", display:"flex", alignItems:"center", justifyContent:"center", padding:"16px" }}
          onClick={closeReportModal}
        >
          <div
            className="report-modal-box"
            onClick={e => e.stopPropagation()}
            style={{
              background:"linear-gradient(160deg,rgba(18,10,28,0.98),rgba(10,4,18,0.99))",
              border:"1.5px solid rgba(245,200,66,0.22)",
              borderRadius:"24px",
              padding:"28px 28px 24px",
              width:"min(480px,94vw)",
              boxShadow:"0 0 60px rgba(245,200,66,0.06), 0 30px 80px rgba(0,0,0,0.75)",
              backdropFilter:"blur(24px)",
              WebkitBackdropFilter:"blur(24px)",
              fontFamily:"'Tajawal','Cairo',sans-serif",
              direction:"rtl",
            }}
          >
            {reportSent ? (
              /* ── Success state ── */
              <div style={{ textAlign:"center", padding:"20px 0" }}>
                <div style={{ fontSize:"3rem", marginBottom:"16px" }}>✅</div>
                <div style={{ color:"#43e97b", fontWeight:800, fontSize:"1.2rem", marginBottom:"8px" }}>
                  شكراً لك!
                </div>
                <div style={{ color:"rgba(255,255,255,0.55)", fontSize:"0.88rem", lineHeight:1.6 }}>
                  تم استلام البلاغ وسيتم مراجعته من فريق حُجّة.
                </div>
                <button
                  onClick={closeReportModal}
                  style={{ marginTop:"24px", background:"linear-gradient(135deg,#f5c842,#ff8c42)", color:"#1a1208", fontWeight:800, fontSize:"0.95rem", padding:"10px 32px", borderRadius:"50px", border:"none", cursor:"pointer", boxShadow:"0 4px 18px rgba(245,200,66,0.35)" }}
                >
                  إغلاق
                </button>
              </div>
            ) : (
              <>
                {/* Header */}
                <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:"20px" }}>
                  <div>
                    <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"0.6rem", fontWeight:800, letterSpacing:"0.14em", textTransform:"uppercase", marginBottom:"4px" }}>إبلاغ عن مشكلة</div>
                    <div style={{ color:"rgba(255,255,255,0.92)", fontWeight:800, fontSize:"1.05rem" }}>ما المشكلة في هذا السؤال؟</div>
                  </div>
                  <button onClick={closeReportModal} style={{ background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)", borderRadius:"50%", width:"32px", height:"32px", color:"rgba(255,255,255,0.45)", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1rem", flexShrink:0 }}>✕</button>
                </div>

                {/* Reason grid */}
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:"8px", marginBottom:"16px" }}>
                  {REPORT_REASONS.map(r => (
                    <button
                      key={r.id}
                      className={`report-reason-btn${reportReason === r.label ? " selected" : ""}`}
                      onClick={() => setReportReason(r.label)}
                      style={{
                        display:"flex", alignItems:"center", gap:"8px",
                        padding:"10px 12px", borderRadius:"12px", textAlign:"right",
                        background: reportReason === r.label ? "rgba(245,200,66,0.18)" : "rgba(255,255,255,0.04)",
                        border: `1.5px solid ${reportReason === r.label ? "rgba(245,200,66,0.70)" : "rgba(255,255,255,0.10)"}`,
                        color: reportReason === r.label ? "#f5c842" : "rgba(255,255,255,0.70)",
                        cursor:"pointer", transition:"all 0.18s",
                        fontFamily:"'Tajawal','Cairo',sans-serif",
                        fontSize:"0.78rem", fontWeight:700,
                      }}
                    >
                      <span style={{ fontSize:"1rem", flexShrink:0 }}>{r.icon}</span>
                      <span style={{ lineHeight:1.3 }}>{r.label}</span>
                    </button>
                  ))}
                </div>

                {/* Optional notes (if "other" or any reason) */}
                {reportReason && (
                  <textarea
                    value={reportDetails}
                    onChange={e => setReportDetails(e.target.value)}
                    placeholder={reportReason === "سبب آخر" ? "اكتب ملاحظتك هنا..." : "ملاحظات إضافية (اختياري)"}
                    rows={2}
                    maxLength={300}
                    style={{
                      width:"100%", background:"rgba(255,255,255,0.05)", border:"1.5px solid rgba(255,255,255,0.12)",
                      borderRadius:"12px", padding:"10px 14px", color:"rgba(255,255,255,0.80)",
                      fontSize:"0.82rem", fontFamily:"'Tajawal','Cairo',sans-serif", outline:"none",
                      resize:"none", marginBottom:"16px", boxSizing:"border-box",
                    }}
                  />
                )}

                {/* Send button */}
                <button
                  onClick={handleSendReport}
                  disabled={!reportReason || reportSending}
                  style={{
                    width:"100%", padding:"12px", borderRadius:"50px", border:"none",
                    background: reportReason ? "linear-gradient(135deg,#f5c842,#ff8c42)" : "rgba(255,255,255,0.08)",
                    color: reportReason ? "#1a1208" : "rgba(255,255,255,0.25)",
                    fontWeight:800, fontSize:"0.98rem", cursor: reportReason ? "pointer" : "not-allowed",
                    fontFamily:"'Tajawal','Cairo',sans-serif",
                    boxShadow: reportReason ? "0 4px 18px rgba(245,200,66,0.35)" : "none",
                    transition:"all 0.22s", opacity: reportSending ? 0.6 : 1,
                  }}
                >
                  {reportSending ? "⏳ جارٍ الإرسال..." : "إرسال البلاغ"}
                </button>
              </>
            )}
          </div>
        </div>
      )}


      {/* ════════════ TOP BAR ════════════ */}
      <header
        data-testid="question-header"
        style={{
          flexShrink:0, position:"relative", zIndex:10,
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"0 clamp(12px,2vw,24px)",
          height:"clamp(48px,6.5vh,62px)",
          background:"rgba(255,255,255,0.07)",
          borderBottom:"1px solid rgba(255,255,255,0.09)",
          backdropFilter:"blur(14px)",
          WebkitBackdropFilter:"blur(14px)",
        }}
      >
        <button
          data-testid="back-to-board"
          onClick={handleBack}
          style={{ display:"flex", alignItems:"center", gap:"6px", color:"rgba(255,255,255,0.40)", fontWeight:700, fontSize:"clamp(0.72rem,1.2vw,0.88rem)", background:"none", border:"none", cursor:"pointer", transition:"color 0.18s", fontFamily:"'Tajawal','Cairo',sans-serif" }}
          onMouseEnter={e=>e.currentTarget.style.color="rgba(255,255,255,0.80)"}
          onMouseLeave={e=>e.currentTarget.style.color="rgba(255,255,255,0.40)"}
        >
          <span>←</span><span>اللوحة</span>
        </button>

        <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"1px" }}>
          <span style={{ color:"rgba(245,200,66,0.85)", fontWeight:800, fontSize:"clamp(0.78rem,1.4vw,1rem)" }}>
            {session?.name || "حُجّة"}
          </span>
          <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
            <span style={{ color:"rgba(255,255,255,0.38)", fontSize:"clamp(0.6rem,0.95vw,0.74rem)", fontWeight:600 }}>{catName}</span>
            <span style={{ color:"rgba(255,255,255,0.15)" }}>·</span>
            <span style={{ color:diffBadge.color, fontSize:"clamp(0.6rem,0.95vw,0.74rem)", fontWeight:800 }}>{diffBadge.label}</span>
          </div>
        </div>

        <div className="q-header-spacer" style={{ width:"clamp(155px,17vw,220px)" }} />
      </header>

      {/* ════════════ MAIN BODY — 3-column ════════════ */}
      <div
        className="question-body-grid"
        style={{
          flex:1, display:"grid",
          gridTemplateColumns:"clamp(155px,17vw,220px) minmax(0,1fr) clamp(155px,17vw,220px)",
          gridTemplateRows:"auto",
          gap:"clamp(8px,1vw,14px)",
          padding:"clamp(10px,1.4vw,16px)",
          minHeight:0,
          alignContent:"start",
          position:"relative", zIndex:10,
        }}
      >
        {/* ── LEFT: Team 1 panel ── */}
        <div className="q-left-col" style={{ display:"flex", flexDirection:"column", gap:"10px" }}>
          {/* Team 1 */}
          <div className="q-team1-panel" style={{
            ...glass,
            borderRight: `4px solid ${isTeam1 ? "#43e97b" : "#f5c842"}`,
            borderRadius:"16px",
            padding:"16px",
            background: isTeam1 ? "rgba(67,233,123,0.08)" : "rgba(255,255,255,0.07)",
            boxShadow: isTeam1 ? "0 0 24px rgba(67,233,123,0.18), 0 8px 32px rgba(0,0,0,0.35)" : "0 8px 32px rgba(0,0,0,0.35)",
            transition:"all 0.35s",
            display:"flex", flexDirection:"column", gap:"8px",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:"7px" }}>
              <div style={{ width:"8px", height:"8px", borderRadius:"50%", background:"#ff6b6b", boxShadow:isTeam1?"0 0 8px #ff6b6b":"none", animation:isTeam1?"pulse 2s infinite":"none", flexShrink:0 }} />
              <span style={{ color:isTeam1?"rgba(255,255,255,0.90)":"rgba(255,255,255,0.50)", fontWeight:800, fontSize:"clamp(0.78rem,1.1vw,0.95rem)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {session?.team1_name || "الفريق الأول"}
              </span>
            </div>
            {isTeam1 && (
              <div style={{ background:"rgba(67,233,123,0.14)", border:"1px solid rgba(67,233,123,0.28)", borderRadius:"8px", padding:"3px 8px", textAlign:"center", color:"#43e97b", fontSize:"0.64rem", fontWeight:800, letterSpacing:"0.04em" }}>
                يجيب الآن
              </div>
            )}
            <div style={{ textAlign:"center" }}>
              <div style={{ color:"#f5c842", fontSize:"clamp(1.9rem,3vw,2.6rem)", fontWeight:900, lineHeight:1, textShadow:"0 2px 12px rgba(245,200,66,0.4)" }}>{teamScores.team1}</div>
              <div style={{ color:"rgba(255,255,255,0.28)", fontSize:"0.62rem", fontWeight:700, marginTop:"2px" }}>نقطة</div>
            </div>
          </div>

          {/* Difficulty */}
          <div style={{ ...glass, padding:"12px", textAlign:"center", background:diffBadge.bg, border:`1px solid ${diffBadge.border}` }}>
            <div style={{ color:diffBadge.color, fontSize:"clamp(1.5rem,2.5vw,2.1rem)", fontWeight:900, lineHeight:1 }}>{question.difficulty}</div>
            <div style={{ color:diffBadge.color, fontSize:"0.62rem", fontWeight:700, opacity:0.75, marginTop:"3px" }}>{diffBadge.label}</div>
          </div>
        </div>

        {/* ── CENTER: Question panel ── */}
        <div className="q-center-col" style={{
          ...glass,
          borderRadius:"24px",
          border:"2px solid rgba(245,200,66,0.35)",
          boxShadow:"0 0 60px rgba(245,200,66,0.08), 0 20px 60px rgba(0,0,0,0.55)",
          display:"flex", flexDirection:"column",
          minHeight:0,
        }}>

          {/* Panel header */}
          <div className="q-panel-header" style={{
            flexShrink:0,
            display:"flex", alignItems:"center", justifyContent:"space-between",
            padding:"clamp(10px,1.4vh,16px) clamp(14px,2vw,22px)",
            background: isTeam1 ? "rgba(255,107,107,0.10)" : "rgba(96,165,250,0.10)",
            borderBottom:`1px solid ${isTeam1 ? "rgba(255,107,107,0.20)" : "rgba(96,165,250,0.20)"}`,
          }}>
            {/* Active team */}
            <div style={{ display:"flex", alignItems:"center", gap:"8px" }}>
              <div style={{ width:"10px", height:"10px", borderRadius:"50%", background: isTeam1?"#ff6b6b":"#60a5fa", boxShadow:`0 0 10px ${isTeam1?"#ff6b6b":"#60a5fa"}`, flexShrink:0 }} />
              <div>
                <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"0.58rem", fontWeight:800, textTransform:"uppercase", letterSpacing:"0.08em" }}>يجيب الآن</div>
                <div style={{ color: isTeam1?"#ff6b6b":"#60a5fa", fontSize:"clamp(0.88rem,1.6vw,1.15rem)", fontWeight:900 }}>{activeTeamName}</div>
              </div>
            </div>

            {/* Timer */}
            <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"6px" }}>
              <div className="q-timer-wrap" style={{ position:"relative", width:"clamp(72px,8.5vw,96px)", height:"clamp(72px,8.5vw,96px)" }}>
                <svg width="100%" height="100%" viewBox="0 0 70 70" style={{ overflow:"visible" }}>
                  <circle cx="35" cy="35" r={R} fill="rgba(0,0,0,0.3)" stroke="rgba(255,255,255,0.08)" strokeWidth="5" />
                  <circle
                    cx="35" cy="35" r={R}
                    fill="none" stroke={timerCol} strokeWidth="5" strokeLinecap="round"
                    strokeDasharray={circ} strokeDashoffset={dash}
                    transform="rotate(-90 35 35)"
                    style={{ transition:"stroke-dashoffset 1s linear, stroke 0.5s ease" }}
                  />
                  <text x="35" y="35" textAnchor="middle" dominantBaseline="central" fill={timerCol} fontSize="17" fontWeight="900" fontFamily="Tajawal,Cairo,sans-serif">
                    {timeLeft}
                  </text>
                </svg>
              </div>
              <div style={{ display:"flex", gap:"5px" }}>
                <button
                  data-testid="timer-pause-resume-btn"
                  onClick={() => setTimerOn(t => !t)}
                  style={{ display:"flex", alignItems:"center", gap:"3px", background:timerOn?"rgba(245,200,66,0.13)":"rgba(67,233,123,0.13)", border:`1px solid ${timerOn?"rgba(245,200,66,0.35)":"rgba(67,233,123,0.35)"}`, color:timerOn?"#f5c842":"#43e97b", borderRadius:"8px", padding:"3px 7px", cursor:"pointer", fontSize:"0.58rem", fontWeight:800, fontFamily:"Tajawal,Cairo,sans-serif" }}
                >
                  {timerOn ? <Pause size={9}/> : <Play size={9}/>}
                  {timerOn?"إيقاف":"تشغيل"}
                </button>
                <button
                  data-testid="timer-reset-btn"
                  onClick={() => { setTimeLeft(TIMER_DURATION); setTimerOn(false); setTensionDone(false); }}
                  style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", color:"rgba(255,255,255,0.55)", borderRadius:"8px", padding:"4px 5px", cursor:"pointer" }}
                  title="إعادة"
                >
                  <RotateCcw size={9}/>
                </button>
                <button
                  data-testid="timer-start-btn"
                  onClick={() => { setTimeLeft(TIMER_DURATION); setTimerOn(true); setTensionDone(false); }}
                  style={{ display:"flex", alignItems:"center", gap:"3px", background:"rgba(67,233,123,0.10)", border:"1px solid rgba(67,233,123,0.28)", color:"rgba(67,233,123,0.80)", borderRadius:"8px", padding:"3px 7px", cursor:"pointer", fontSize:"0.58rem", fontWeight:800, fontFamily:"Tajawal,Cairo,sans-serif" }}
                >
                  <Play size={9}/>ابدأ
                </button>
              </div>
            </div>

            {/* Points badge */}
            <div style={{ background:"linear-gradient(135deg,#f5c842,#ff8c42)", color:"#1a1208", fontWeight:900, fontSize:"clamp(1.3rem,2.6vw,2.1rem)", borderRadius:"50px", padding:"8px 18px", textAlign:"center", boxShadow:"0 4px 16px rgba(245,200,66,0.40)", lineHeight:1 }}>
              {question.difficulty}
              <div style={{ fontSize:"0.55em", fontWeight:700, opacity:0.7, marginTop:"2px" }}>نقطة</div>
            </div>
          </div>

          {/* Panel body: image + text */}
          <div className="q-panel-body" style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"clamp(6px,1vh,12px) clamp(10px,1.5vw,20px)", minHeight:0, gap:"clamp(4px,0.7vh,10px)", position:"relative" }}>

            {/* ── REPORT BUTTON ── */}
            <button
              onClick={() => setShowReportModal(true)}
              aria-label="الإبلاغ عن السؤال"
              title="الإبلاغ عن السؤال"
              style={{
                position:"absolute", top:"10px", left:"12px",
                display:"flex", alignItems:"center", gap:"6px",
                padding:"7px 14px 7px 11px", borderRadius:"10px",
                border:"1.5px solid rgba(252,165,165,0.35)",
                background:"rgba(239,68,68,0.10)", color:"rgba(252,165,165,0.82)",
                cursor:"pointer", fontSize:"0.75rem", fontWeight:800,
                fontFamily:"'Tajawal','Cairo',sans-serif",
                transition:"background 0.18s, border-color 0.18s, color 0.18s, box-shadow 0.18s",
                zIndex:2, minHeight:"36px",
                outline:"none",
                boxShadow:"0 2px 8px rgba(239,68,68,0.10)",
                letterSpacing:"0.01em",
              }}
              onMouseEnter={e => { e.currentTarget.style.background="rgba(239,68,68,0.20)"; e.currentTarget.style.borderColor="rgba(252,165,165,0.65)"; e.currentTarget.style.color="rgba(255,185,185,1)"; e.currentTarget.style.boxShadow="0 4px 16px rgba(239,68,68,0.22)"; }}
              onMouseLeave={e => { e.currentTarget.style.background="rgba(239,68,68,0.10)"; e.currentTarget.style.borderColor="rgba(252,165,165,0.35)"; e.currentTarget.style.color="rgba(252,165,165,0.82)"; e.currentTarget.style.boxShadow="0 2px 8px rgba(239,68,68,0.10)"; }}
              onMouseDown={e => { e.currentTarget.style.transform="scale(0.96)"; e.currentTarget.style.transition="transform 0.08s"; }}
              onMouseUp={e => { e.currentTarget.style.transform="scale(1)"; e.currentTarget.style.transition="background 0.18s, border-color 0.18s, color 0.18s, box-shadow 0.18s"; }}
              onFocus={e => { e.currentTarget.style.outline="2px solid rgba(252,165,165,0.50)"; e.currentTarget.style.outlineOffset="2px"; }}
              onBlur={e => { e.currentTarget.style.outline="none"; }}
            >
              <Flag size={14} strokeWidth={2.2} />
              <span>الإبلاغ عن السؤال</span>
            </button>
            {isSecret && !showAnswer ? (
              <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"8px", position:"relative", width:"100%" }}>

                {/* ── Transient "بدأت الجولة" badge — auto-disappears after 2 s ── */}
                {readyBadge && (
                  <div className="ready-badge" style={{
                    position:"absolute", top:"-4px", left:"50%", transform:"translateX(-50%)",
                    zIndex:10, pointerEvents:"none", whiteSpace:"nowrap",
                    background:"linear-gradient(135deg,rgba(67,233,123,0.97),rgba(40,180,80,0.97))",
                    border:"1px solid rgba(67,233,123,0.75)",
                    borderRadius:"50px", padding:"6px 20px",
                    color:"#062710", fontSize:"0.82rem", fontWeight:900,
                    boxShadow:"0 0 24px rgba(67,233,123,0.55)",
                    fontFamily:"'Tajawal','Cairo',sans-serif",
                  }}>
                    ✓ بدأت الجولة!
                  </div>
                )}

                {/* Status heading */}
                <div style={{ color:"#f5c842", fontWeight:900, textAlign:"center", fontSize:"clamp(0.9rem,1.8vw,1.25rem)", marginTop:"4px" }}>
                  {roundReady ? "📱 اللاعب جاهز — العداد يعمل" : "امسح الباركود من الجوال"}
                </div>

                {!roundReady && (
                  <div style={{ color:"rgba(255,255,255,0.40)", fontSize:"0.75rem", textAlign:"center" }}>
                    اللاعب يضغط «جاهز» لبدء العداد
                  </div>
                )}

                {/* QR — always visible regardless of readyState */}
                <div style={{
                  background:"white", padding:"12px", borderRadius:"14px",
                  boxShadow: roundReady
                    ? "0 0 28px rgba(67,233,123,0.35)"
                    : "0 0 44px rgba(245,200,66,0.28)",
                  transition:"box-shadow 0.5s ease",
                }}>
                  <QRCodeSVG value={secretUrl} size={Math.min(160, Math.floor(window.innerHeight * 0.18))} data-testid="qr-code" />
                </div>

                <p style={{ color:"rgba(255,255,255,0.12)", fontSize:"9px", fontFamily:"monospace", wordBreak:"break-all", maxWidth:"220px", textAlign:"center", margin:0 }}>{secretUrl}</p>

                {/* Status row */}
                {!roundReady ? (
                  <>
                    <div style={{ display:"flex", alignItems:"center", gap:"7px", background:"rgba(245,200,66,0.08)", border:"1px solid rgba(245,200,66,0.22)", borderRadius:"50px", padding:"6px 14px", color:"rgba(245,200,66,0.75)", fontSize:"0.70rem", fontWeight:700, fontFamily:"'Tajawal','Cairo',sans-serif" }}>
                      <span style={{ width:"7px", height:"7px", borderRadius:"50%", background:"#f5c842", display:"inline-block", flexShrink:0, animation:"turnPulse 1.4s ease-in-out infinite" }} />
                      بانتظار «جاهز» من الجوال...
                    </div>
                    <button
                      type="button"
                      onClick={handleHostReady}
                      style={{ background:"linear-gradient(135deg,#f5c842,#ff8c42)", border:"none", borderRadius:"50px", padding:"8px 24px", fontSize:"clamp(0.80rem,1.3vw,0.92rem)", fontWeight:800, color:"#1a1208", cursor:"pointer", boxShadow:"0 4px 18px rgba(245,200,66,0.38)", fontFamily:"'Tajawal','Cairo',sans-serif", marginTop:"2px" }}
                    >
                      جاهز — ابدأ الوقت ✓
                    </button>
                  </>
                ) : (
                  <div style={{ display:"flex", alignItems:"center", gap:"7px", background:"rgba(67,233,123,0.12)", border:"1px solid rgba(67,233,123,0.35)", borderRadius:"50px", padding:"6px 14px", color:"#43e97b", fontSize:"0.70rem", fontWeight:800, fontFamily:"'Tajawal','Cairo',sans-serif" }}>
                    <span style={{ width:"7px", height:"7px", borderRadius:"50%", background:"#43e97b", display:"inline-block", flexShrink:0 }} />
                    العداد يعمل ✓
                  </div>
                )}
              </div>
            ) : showAnswer ? (
              /* ══ ANSWER STATE — same panel, content flips ══ */
              <div key="answer-state" className="answer-reveal" style={{ width:"100%", display:"flex", flexDirection:"column", alignItems:"center", gap:"clamp(8px,1.2vh,14px)" }}>

                {/* Answer label */}
                <div style={{ color:"rgba(245,200,66,0.55)", fontSize:"0.72rem", fontWeight:800, letterSpacing:"0.18em", textTransform:"uppercase" }}>
                  ✦ الإجابة الصحيحة ✦
                </div>

                {/* Answer image — same slot as question image */}
                {hasAnswerImg && (
                  <div
                    data-testid="question-image-container"
                    style={{ width:"100%", display:"flex", justifyContent:"center", alignItems:"center", flexShrink:0 }}
                  >
                    <div style={{ position:"relative", display:"inline-flex", alignItems:"center" }}>
                      <img
                        src={currentQuestion.answer_image_url}
                        alt="answer"
                        data-testid="answer-image"
                        loading="eager"
                        className="answer-img-reveal"
                        onClick={() => setZoomedImage(currentQuestion.answer_image_url)}
                        onError={e => { e.target.parentElement.parentElement.style.display="none"; }}
                        style={{
                          display: "block",
                          maxHeight: "clamp(180px,40vh,430px)",
                          maxWidth: "min(100%, 860px)",
                          width: "auto",
                          objectFit: "contain",
                          borderRadius: "14px",
                          border: "1.5px solid rgba(245,200,66,0.45)",
                          boxShadow: "0 0 0 3px rgba(245,200,66,0.10), 0 10px 32px rgba(0,0,0,0.65)",
                          cursor: "zoom-in",
                          transition: "transform 0.25s",
                        }}
                        onMouseEnter={e => e.target.style.transform="scale(1.02)"}
                        onMouseLeave={e => e.target.style.transform="scale(1)"}
                      />
                      <div style={{ position:"absolute", bottom:"6px", left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.60)", backdropFilter:"blur(4px)", WebkitBackdropFilter:"blur(4px)", borderRadius:"6px", padding:"2px 8px", color:"rgba(245,200,66,0.75)", fontSize:"0.58rem", fontWeight:800, pointerEvents:"none", whiteSpace:"nowrap" }}>
                        <ZoomIn size={9} style={{ display:"inline", marginLeft:"3px", verticalAlign:"middle" }} />
                        اضغط للتكبير
                      </div>
                    </div>
                  </div>
                )}

                {/* Answer text */}
                {hasAnswerTxt && (
                  <div
                    data-testid="answer-text"
                    dir={answerDir}
                    style={{
                      textAlign: "center",
                      direction: answerDir,
                      unicodeBidi: "isolate",
                      color: "#ffffff",
                      fontWeight: 900,
                      lineHeight: 1.2,
                      textShadow: "0 0 40px rgba(245,200,66,0.35), 0 2px 12px rgba(0,0,0,0.70)",
                      maxWidth: "760px",
                      fontSize: hasAnswerImg
                        ? "clamp(1.5rem,3vw,2.4rem)"
                        : "clamp(2rem,5vw,3.6rem)",
                    }}
                  >
                    {answerText}
                  </div>
                )}
              </div>
            ) : (
              /* ══ QUESTION STATE ══ */
              <>
                {/* ── QUESTION TEXT ── */}
                <p
                  data-testid="question-text"
                  dir={questionDir}
                  style={{
                    margin: "0 auto",
                    textAlign: "center",
                    direction: questionDir,
                    unicodeBidi: "isolate",
                    color: "#ffffff",
                    fontWeight: 700,
                    lineHeight: 1.5,
                    textShadow: "0 2px 16px rgba(0,0,0,0.55)",
                    maxWidth: "760px",
                    flexShrink: 0,
                    fontSize: (currentQuestion?.image_url || currentQuestion?.audio_url)
                      ? "clamp(1.22rem,2.55vw,1.96rem)"
                      : "clamp(2.02rem,4.65vw,3.6rem)",
                  }}
                >
                  {questionText}
                </p>

                {/* ── AUDIO PLAYER — for audio question type ── */}
                {currentQuestion?.audio_url && (
                  <AudioPlayer url={currentQuestion.audio_url} />
                )}

                {/* ── QUESTION IMAGE ── */}
                {currentQuestion?.image_url && (
                  <div
                    data-testid="question-image-container"
                    style={{ width:"100%", display:"flex", justifyContent:"center", alignItems:"center", flexShrink: 0, position:"relative" }}
                  >
                    {/* Skeleton shown while image loads */}
                    {!imgLoaded && (
                      <div style={{
                        width: "min(520px, 92%)",
                        height: "clamp(180px,32vh,340px)",
                        borderRadius: "14px",
                        background: "linear-gradient(90deg, rgba(255,255,255,0.06) 25%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.06) 75%)",
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.4s infinite",
                        border: "1.5px solid rgba(245,200,66,0.15)",
                      }} />
                    )}
                    {/*
                      BUG FIX: <img> must always be in the DOM — never inside display:none.
                      Safari does not reliably fire onLoad for images inside display:none containers.
                      When not yet loaded: position:absolute + visibility:hidden keeps it out of layout
                      while still allowing the browser to load it and fire onLoad.
                    */}
                    <div style={{
                      position: imgLoaded ? "relative" : "absolute",
                      visibility: imgLoaded ? "visible" : "hidden",
                      display: "inline-flex", alignItems:"center"
                    }}>
                      <img
                        ref={imgElemRef}
                        src={currentQuestion.image_url + (imgRetries > 0 ? `?r=${imgRetries}` : "")}
                        alt="question"
                        data-testid="question-image"
                        loading="eager"
                        decoding="async"
                        onLoad={() => setImgLoaded(true)}
                        onError={e => {
                          const MAX = 3;
                          if (imgRetries < MAX) {
                            const attempt = imgRetries + 1;
                            setTimeout(() => {
                              setImgRetries(attempt);
                            }, attempt * 1200);
                          } else {
                            // All retries exhausted — hide container permanently
                            e.target.closest("[data-testid='question-image-container']").style.display = "none";
                            console.error("[HUJJAH] question image failed after", MAX, "retries:", currentQuestion.image_url);
                          }
                        }}
                        onClick={() => setZoomedImage(currentQuestion.image_url)}
                        style={{
                          display: "block",
                          maxHeight: "clamp(180px,40vh,420px)",
                          maxWidth: "min(100%, 860px)",
                          width: "auto",
                          objectFit: "contain",
                          borderRadius: "14px",
                          border: "1.5px solid rgba(245,200,66,0.28)",
                          boxShadow: "0 0 0 3px rgba(245,200,66,0.08), 0 10px 32px rgba(0,0,0,0.65)",
                          cursor: "zoom-in",
                          transition: "transform 0.25s",
                        }}
                        onMouseEnter={e=>e.target.style.transform="scale(1.02)"}
                        onMouseLeave={e=>e.target.style.transform="scale(1)"}
                      />
                      <div style={{ position:"absolute", bottom:"6px", left:"50%", transform:"translateX(-50%)", background:"rgba(0,0,0,0.60)", backdropFilter:"blur(4px)", WebkitBackdropFilter:"blur(4px)", borderRadius:"6px", padding:"2px 8px", color:"rgba(245,200,66,0.75)", fontSize:"0.58rem", fontWeight:800, pointerEvents:"none", whiteSpace:"nowrap" }}>
                        <ZoomIn size={9} style={{ display:"inline", marginLeft:"3px", verticalAlign:"middle" }} />
                        اضغط للتكبير
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Panel footer */}
          <div className="q-panel-footer" style={{ flexShrink:0, padding:"clamp(10px,1.5vh,16px) clamp(16px,3vw,32px)", borderTop:"1px solid rgba(255,255,255,0.07)", display:"flex", justifyContent:"center", alignItems:"center" }}>
            {!showAnswer ? (
              /* ── Pre-reveal OR toggle back to question ── */
              <div style={{ display:"flex", alignItems:"center", gap:"12px" }}>
                {revealed && (
                  <button
                    onClick={() => setShowAnswer(true)}
                    style={{
                      background:"rgba(245,200,66,0.10)", border:"1.5px solid rgba(245,200,66,0.30)",
                      borderRadius:"50px", padding:"clamp(9px,1.3vh,13px) clamp(18px,3vw,28px)",
                      fontSize:"clamp(0.82rem,1.3vw,0.98rem)", fontWeight:800,
                      color:"rgba(245,200,66,0.85)", cursor:"pointer",
                      transition:"all 0.22s", fontFamily:"'Tajawal','Cairo',sans-serif",
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.background="rgba(245,200,66,0.18)";e.currentTarget.style.borderColor="rgba(245,200,66,0.55)";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="rgba(245,200,66,0.10)";e.currentTarget.style.borderColor="rgba(245,200,66,0.30)";}}
                  >
                    ✅ عرض الإجابة
                  </button>
                )}
                <button
                  data-testid="reveal-answer-btn"
                  className="reveal-btn"
                  onClick={handleReveal}
                  style={{
                    background:"linear-gradient(135deg,#f5c842,#ff8c42)",
                    border:"none", borderRadius:"50px",
                    padding:"clamp(10px,1.5vh,15px) clamp(32px,5.5vw,56px)",
                    fontSize:"clamp(0.92rem,1.6vw,1.12rem)",
                    fontWeight:800, color:"#1a1208",
                    cursor:"pointer",
                    boxShadow:"0 6px 22px rgba(245,200,66,0.38)",
                    transition:"transform 0.22s, box-shadow 0.22s",
                    fontFamily:"'Tajawal','Cairo',sans-serif",
                    display: revealed ? "none" : "block",
                  }}
                >
                  كشف الإجابة
                </button>
              </div>
            ) : !assigned ? (
              isSolo ? (
                /* ── SOLO: صح / خطأ ── */
                <div className="answer-reveal" style={{ width:"100%", display:"flex", flexDirection:"column", alignItems:"center", gap:"14px" }}>
                  <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"0.72rem", fontWeight:800, letterSpacing:"0.10em" }}>هل أجبت صح؟</div>
                  <div style={{ display:"flex", gap:"16px", justifyContent:"center" }}>
                    <button
                      data-testid="solo-correct-btn"
                      onClick={() => handleAssign(1)}
                      style={{
                        background:"linear-gradient(135deg,rgba(22,101,52,0.95),rgba(15,60,30,0.98))",
                        border:"2px solid rgba(74,222,128,0.50)", borderRadius:"20px",
                        padding:"clamp(14px,2.5vh,22px) clamp(28px,4vw,52px)",
                        cursor:"pointer", transition:"transform 0.18s",
                        display:"flex", flexDirection:"column", alignItems:"center", gap:"4px",
                        fontFamily:"'Tajawal','Cairo',sans-serif",
                      }}
                      onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.05)";}}
                      onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";}}
                    >
                      <span style={{ color:"#4ade80", fontWeight:900, fontSize:"clamp(1.6rem,3vw,2.5rem)", lineHeight:1 }}>✓</span>
                      <span style={{ color:"rgba(134,239,172,0.90)", fontWeight:800, fontSize:"clamp(0.85rem,1.3vw,1rem)" }}>صح</span>
                      <span style={{ color:"#4ade80", fontWeight:900, fontSize:"clamp(1rem,1.8vw,1.4rem)" }}>+{currentQuestion.difficulty}{doubleActive?" ×2":""}</span>
                    </button>

                    <button
                      data-testid="solo-wrong-btn"
                      onClick={handleSkip}
                      style={{
                        background:"linear-gradient(135deg,rgba(127,29,29,0.95),rgba(80,15,15,0.98))",
                        border:"2px solid rgba(248,113,113,0.40)", borderRadius:"20px",
                        padding:"clamp(14px,2.5vh,22px) clamp(28px,4vw,52px)",
                        cursor:"pointer", transition:"transform 0.18s",
                        display:"flex", flexDirection:"column", alignItems:"center", gap:"4px",
                        fontFamily:"'Tajawal','Cairo',sans-serif",
                      }}
                      onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.05)";}}
                      onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";}}
                    >
                      <span style={{ color:"#f87171", fontWeight:900, fontSize:"clamp(1.6rem,3vw,2.5rem)", lineHeight:1 }}>✕</span>
                      <span style={{ color:"rgba(252,165,165,0.90)", fontWeight:800, fontSize:"clamp(0.85rem,1.3vw,1rem)" }}>خطأ</span>
                      <span style={{ color:"rgba(248,113,113,0.55)", fontWeight:700, fontSize:"clamp(0.82rem,1.2vw,0.95rem)" }}>0 نقطة</span>
                    </button>
                  </div>
                  <button
                    onClick={() => setShowAnswer(false)}
                    style={{
                      background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.18)",
                      borderRadius:"50px", padding:"clamp(8px,1.4vh,13px) clamp(14px,2vw,22px)",
                      fontSize:"clamp(0.78rem,1.1vw,0.92rem)", fontWeight:800,
                      color:"rgba(255,255,255,0.60)", cursor:"pointer",
                      transition:"all 0.20s", fontFamily:"'Tajawal','Cairo',sans-serif",
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.13)";e.currentTarget.style.color="rgba(255,255,255,0.85)";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.color="rgba(255,255,255,0.60)";}}
                  >
                    👁️ عرض السؤال
                  </button>
                </div>
              ) : (
              /* ── Standard: team assignment ── */
              <div className="answer-reveal" style={{ width:"100%", display:"flex", flexDirection:"column", alignItems:"center", gap:"10px" }}>
                <div style={{ color:"rgba(255,255,255,0.35)", fontSize:"0.72rem", fontWeight:800, letterSpacing:"0.10em" }}>من أجاب صح؟</div>
                <div className="q-score-btns" style={{ display:"flex", gap:"clamp(6px,1.2vw,12px)", justifyContent:"center", flexWrap:"wrap", width:"100%" }}>
                  <button
                    data-testid="assign-team1-btn"
                    onClick={() => handleAssign(1)}
                    style={{
                      background:"linear-gradient(135deg,rgba(185,28,28,0.95),rgba(100,20,20,0.98))",
                      border:"2px solid rgba(248,113,113,0.50)", borderRadius:"16px",
                      padding:"clamp(8px,1.4vh,13px) clamp(16px,2.8vw,36px)",
                      cursor:"pointer", transition:"transform 0.18s",
                      display:"flex", flexDirection:"column", alignItems:"center",
                      fontFamily:"'Tajawal','Cairo',sans-serif",
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.05)";}}
                    onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";}}
                  >
                    <span style={{ color:"rgba(255,200,200,0.90)", fontWeight:800, fontSize:"clamp(0.78rem,1.2vw,0.95rem)", lineHeight:1.2 }}>🔴 {session?.team1_name}</span>
                    <span style={{ color:"#fca5a5", fontWeight:900, fontSize:"clamp(1.3rem,2.4vw,2rem)", lineHeight:1 }}>+{currentQuestion.difficulty}{doubleActive?" ×2":""}</span>
                  </button>

                  <button
                    data-testid="assign-team2-btn"
                    onClick={() => handleAssign(2)}
                    style={{
                      background:"linear-gradient(135deg,rgba(29,78,216,0.95),rgba(20,40,130,0.98))",
                      border:"2px solid rgba(96,165,250,0.50)", borderRadius:"16px",
                      padding:"clamp(8px,1.4vh,13px) clamp(16px,2.8vw,36px)",
                      cursor:"pointer", transition:"transform 0.18s",
                      display:"flex", flexDirection:"column", alignItems:"center",
                      fontFamily:"'Tajawal','Cairo',sans-serif",
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.transform="scale(1.05)";}}
                    onMouseLeave={e=>{e.currentTarget.style.transform="scale(1)";}}
                  >
                    <span style={{ color:"rgba(190,220,255,0.90)", fontWeight:800, fontSize:"clamp(0.78rem,1.2vw,0.95rem)", lineHeight:1.2 }}>{session?.team2_name} 🔵</span>
                    <span style={{ color:"#93c5fd", fontWeight:900, fontSize:"clamp(1.3rem,2.4vw,2rem)", lineHeight:1 }}>+{currentQuestion.difficulty}{doubleActive?" ×2":""}</span>
                  </button>

                  <button
                    data-testid="skip-points-btn"
                    onClick={handleSkip}
                    className="next-btn"
                    style={{
                      background:"rgba(255,255,255,0.07)", border:"1px solid rgba(255,255,255,0.12)",
                      color:"rgba(255,255,255,0.45)", borderRadius:"50px",
                      padding:"clamp(8px,1.4vh,13px) clamp(12px,1.8vw,18px)",
                      fontSize:"clamp(0.78rem,1.1vw,0.92rem)", fontWeight:700,
                      cursor:"pointer", transition:"all 0.22s", alignSelf:"center",
                      fontFamily:"'Tajawal','Cairo',sans-serif",
                    }}
                  >
                    لا أحد
                  </button>

                  <button
                    onClick={() => setShowAnswer(false)}
                    style={{
                      background:"rgba(255,255,255,0.06)", border:"1.5px solid rgba(255,255,255,0.18)",
                      borderRadius:"50px",
                      padding:"clamp(8px,1.4vh,13px) clamp(14px,2vw,22px)",
                      fontSize:"clamp(0.78rem,1.1vw,0.92rem)", fontWeight:800,
                      color:"rgba(255,255,255,0.60)", cursor:"pointer",
                      transition:"all 0.20s", alignSelf:"center",
                      fontFamily:"'Tajawal','Cairo',sans-serif",
                    }}
                    onMouseEnter={e=>{e.currentTarget.style.background="rgba(255,255,255,0.13)";e.currentTarget.style.color="rgba(255,255,255,0.85)";}}
                    onMouseLeave={e=>{e.currentTarget.style.background="rgba(255,255,255,0.06)";e.currentTarget.style.color="rgba(255,255,255,0.60)";}}
                  >
                    👁️ عرض السؤال
                  </button>
                </div>
              </div>
              )
            ) : (
              /* ── Continue ── */
              <div className="answer-reveal" style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:"10px" }}>
                {scoredTeam && (
                  <div style={{ color:"#43e97b", fontWeight:800, fontSize:"clamp(0.82rem,1.4vw,1.05rem)", textShadow:"0 0 16px rgba(67,233,123,0.5)" }}>
                    ✓ +{question.difficulty} نقطة{isSolo ? "" : ` ← ${scoredTeam === 1 ? session?.team1_name : session?.team2_name}`}
                  </div>
                )}
                <button
                  data-testid="continue-btn"
                  onClick={handleBack}
                  style={{
                    background:"linear-gradient(135deg,#f5c842,#ff8c42)",
                    color:"#1a1208", fontWeight:800,
                    fontSize:"clamp(0.95rem,1.6vw,1.15rem)",
                    padding:"clamp(10px,1.6vh,16px) clamp(32px,5vw,60px)",
                    borderRadius:"50px", border:"none", cursor:"pointer",
                    boxShadow:"0 6px 28px rgba(245,200,66,0.45)",
                    transition:"transform 0.18s, box-shadow 0.18s",
                    fontFamily:"'Tajawal','Cairo',sans-serif",
                  }}
                  onMouseEnter={e=>{e.currentTarget.style.transform="translateY(-2px)";e.currentTarget.style.boxShadow="0 10px 38px rgba(245,200,66,0.60)";}}
                  onMouseLeave={e=>{e.currentTarget.style.transform="translateY(0)";e.currentTarget.style.boxShadow="0 6px 28px rgba(245,200,66,0.45)";}}
                >
                  العودة للوحة ←
                </button>
              </div>
            )}
          </div>
        </div>

        {/* ── RIGHT: Team 2 panel + lifelines ── */}
        <div className="q-right-col" style={{ display:"flex", flexDirection:"column", gap:"10px", minHeight:0 }}>
          {/* Team 2 */}
          <div className="q-team2-panel" style={{
            ...glass,
            borderRight: `4px solid ${!isTeam1 ? "#43e97b" : "#f5c842"}`,
            borderRadius:"16px",
            padding:"16px",
            background: !isTeam1 ? "rgba(67,233,123,0.08)" : "rgba(255,255,255,0.07)",
            boxShadow: !isTeam1 ? "0 0 24px rgba(67,233,123,0.18), 0 8px 32px rgba(0,0,0,0.35)" : "0 8px 32px rgba(0,0,0,0.35)",
            transition:"all 0.35s",
            display:"flex", flexDirection:"column", gap:"8px",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:"7px" }}>
              <div style={{ width:"8px", height:"8px", borderRadius:"50%", background:"#60a5fa", boxShadow:!isTeam1?"0 0 8px #60a5fa":"none", flexShrink:0 }} />
              <span style={{ color:!isTeam1?"rgba(255,255,255,0.90)":"rgba(255,255,255,0.50)", fontWeight:800, fontSize:"clamp(0.78rem,1.1vw,0.95rem)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                {session?.team2_name || "الفريق الثاني"}
              </span>
            </div>
            {!isTeam1 && (
              <div style={{ background:"rgba(67,233,123,0.14)", border:"1px solid rgba(67,233,123,0.28)", borderRadius:"8px", padding:"3px 8px", textAlign:"center", color:"#43e97b", fontSize:"0.64rem", fontWeight:800 }}>
                يجيب الآن
              </div>
            )}
            <div style={{ textAlign:"center" }}>
              <div style={{ color:"#f5c842", fontSize:"clamp(1.9rem,3vw,2.6rem)", fontWeight:900, lineHeight:1, textShadow:"0 2px 12px rgba(245,200,66,0.4)" }}>{teamScores.team2}</div>
              <div style={{ color:"rgba(255,255,255,0.28)", fontSize:"0.62rem", fontWeight:700, marginTop:"2px" }}>نقطة</div>
            </div>
          </div>

          {/* ── LIFELINES (وسائل المساعدة) ── */}
          <div className="q-lifelines" style={{ ...glass, padding:"16px 12px", flex:1, display:"flex", flexDirection:"column" }}>
            <div style={{ textAlign:"center", color:"rgba(255,255,255,0.40)", fontSize:"0.58rem", fontWeight:800, letterSpacing:"0.12em", textTransform:"uppercase", marginBottom:"12px" }}>
              وسائل المساعدة
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:"8px", flex:1 }}>
              {[
                {
                  key:     "changeQuestion",
                  icon:    changingQ
                    ? <RotateCcw size={20} strokeWidth={2.2} style={{animation:"spin 0.9s linear infinite"}}/>
                    : <RotateCcw size={20} strokeWidth={2.2}/>,
                  badge:   null,
                  label:   "تبديل السؤال",
                  tooltip: "استبدل السؤال بسؤال جديد من نفس الفئة والنقاط",
                  color:   "#93c5fd",
                  glow:    "rgba(147,197,253,0.28)",
                  rgb:     "147,197,253",
                  isUsed:  !lifelinesState[activeTeam]?.changeQuestion || assigned || showAnswer,
                  isDouble: false,
                  onClick: handleChangeQuestion,
                },
                {
                  key:     "doublePoints",
                  icon:    <Zap size={20} strokeWidth={2.2}/>,
                  badge:   "×2",
                  label:   doubleActive ? "مفعّلة ✓" : lifelinesState[activeTeam]?.doublePoints ? "من البورد" : "استُخدمت",
                  tooltip: doubleActive
                    ? "النقاط ستُضاعف عند الإجابة الصحيحة"
                    : !lifelinesState[activeTeam]?.doublePoints
                    ? "مضاعفة النقاط مستخدمة مسبقاً"
                    : "فعّلها من البورد قبل فتح السؤال",
                  color:   "#fcd34d",
                  glow:    "rgba(252,211,77,0.28)",
                  rgb:     "252,211,77",
                  isUsed:  !doubleActive && !lifelinesState[activeTeam]?.doublePoints,
                  isDouble: true,
                  onClick: handleDoublePoints,
                },
                {
                  key:     "extraTime",
                  icon:    <Clock size={20} strokeWidth={2.2}/>,
                  badge:   "+25s",
                  label:   "زيادة الوقت",
                  tooltip: `تضيف ${EXTRA_TIME_SECONDS} ثانية للوقت المتبقي الحالي`,
                  color:   "#6ee7b7",
                  glow:    "rgba(110,231,183,0.28)",
                  rgb:     "110,231,183",
                  isUsed:  !lifelinesState[activeTeam]?.extraTime || assigned || showAnswer,
                  isDouble: false,
                  onClick: handleExtraTime,
                },
              ].map(ll => {
                const isDoubleOn     = ll.isDouble && doubleActive;
                const isDoubleLocked = ll.isDouble && !doubleActive;

                let cardBg, cardBorder, cardColor, cardOpacity, cardCursor, cardShadow;
                if (ll.isUsed) {
                  cardBg = "rgba(255,255,255,0.03)"; cardBorder = "rgba(255,255,255,0.07)";
                  cardColor = "rgba(255,255,255,0.18)"; cardOpacity = 0.38;
                  cardCursor = "not-allowed"; cardShadow = "none";
                } else if (isDoubleOn) {
                  cardBg = "rgba(252,211,77,0.12)";
                  cardBorder = "rgba(252,211,77,0.55)";
                  cardColor = "#fcd34d"; cardOpacity = 1;
                  cardCursor = "default"; cardShadow = `0 0 20px rgba(252,211,77,0.22), inset 0 1px 0 rgba(252,211,77,0.15)`;
                } else if (isDoubleLocked) {
                  cardBg = "rgba(255,255,255,0.03)"; cardBorder = "rgba(252,211,77,0.15)";
                  cardColor = "rgba(252,211,77,0.30)"; cardOpacity = 1;
                  cardCursor = "pointer"; cardShadow = "none";
                } else {
                  cardBg = `rgba(${ll.rgb},0.10)`;
                  cardBorder = `rgba(${ll.rgb},0.38)`;
                  cardColor = ll.color; cardOpacity = 1;
                  cardCursor = "pointer"; cardShadow = `0 0 14px ${ll.glow}, inset 0 1px 0 rgba(${ll.rgb},0.12)`;
                }

                return (
                  <div key={ll.key} style={{ position:"relative", flex:1, display:"flex", flexDirection:"column" }}
                    onMouseEnter={e => {
                      const tip = e.currentTarget.querySelector(".ll-tip");
                      if (tip) { tip.style.opacity="1"; tip.style.transform="translateY(0)"; }
                      if (!ll.isUsed && !isDoubleOn) {
                        const btn = e.currentTarget.querySelector("button");
                        if (btn) { btn.style.boxShadow=`0 0 24px ${ll.glow}, 0 4px 16px rgba(0,0,0,0.28), inset 0 1px 0 rgba(${ll.rgb},0.18)`; btn.style.borderColor=`rgba(${ll.rgb},0.65)`; }
                      }
                    }}
                    onMouseLeave={e => {
                      const tip = e.currentTarget.querySelector(".ll-tip");
                      if (tip) { tip.style.opacity="0"; tip.style.transform="translateY(-4px)"; }
                      const btn = e.currentTarget.querySelector("button");
                      if (btn) { btn.style.boxShadow=cardShadow; btn.style.borderColor=`1px solid ${cardBorder}`; }
                    }}
                  >
                    <button
                      onClick={ll.onClick}
                      aria-label={ll.tooltip}
                      className={`lifeline-btn${ll.isUsed ? " ll-used" : ""}`}
                      style={{
                        width:"100%", flex:1, display:"flex", flexDirection:"row", alignItems:"center",
                        gap:"10px", padding:"16px 14px", borderRadius:"14px",
                        background: cardBg, border:`1px solid ${cardBorder}`,
                        color: cardColor, cursor: cardCursor, opacity: cardOpacity,
                        boxShadow: cardShadow, backdropFilter:"blur(12px)",
                        WebkitBackdropFilter:"blur(12px)",
                        fontFamily:"Cairo,sans-serif", transition:"border-color 0.18s, box-shadow 0.18s",
                        minHeight:"58px",
                      }}
                    >
                      {/* icon box */}
                      <div style={{
                        flexShrink:0, width:"38px", height:"38px", borderRadius:"10px",
                        background: ll.isUsed ? "rgba(255,255,255,0.04)" : `rgba(${ll.rgb},0.14)`,
                        border: `1px solid ${ll.isUsed ? "rgba(255,255,255,0.06)" : `rgba(${ll.rgb},0.22)`}`,
                        display:"flex", alignItems:"center", justifyContent:"center",
                      }}>
                        {ll.icon}
                      </div>
                      {/* text */}
                      <div style={{ flex:1, minWidth:0, textAlign:"right" }}>
                        <div style={{ fontSize:"0.70rem", fontWeight:900, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis", letterSpacing:"0.01em" }}>
                          {ll.isUsed ? "استُخدمت" : ll.label}
                        </div>
                        {!ll.isUsed && ll.badge && (
                          <div style={{ fontSize:"0.60rem", fontWeight:800, opacity:0.70, marginTop:"2px", letterSpacing:"0.04em" }}>
                            {ll.badge}
                          </div>
                        )}
                      </div>
                    </button>
                    <div className="ll-tip" style={{
                      position:"absolute", top:"calc(100% + 6px)", right:0, left:0, zIndex:99,
                      background:"rgba(8,4,18,0.96)", border:"1px solid rgba(255,255,255,0.13)",
                      borderRadius:"10px", padding:"8px 10px",
                      fontSize:"0.65rem", color:"rgba(255,255,255,0.92)", fontWeight:700,
                      textAlign:"center", lineHeight:1.6,
                      pointerEvents:"none", opacity:0,
                      transform:"translateY(-4px)", transition:"opacity 0.18s, transform 0.18s",
                      backdropFilter:"blur(12px)",
                      WebkitBackdropFilter:"blur(12px)",
                    }}>
                      {ll.tooltip}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
